// src/components/LostItemsGallery.js
import React, { useState, useEffect } from 'react';
import { Auth } from 'aws-amplify';
import { getLostItemsFromDynamoDB } from '../dynamoService';

/* ── tiny icons as SVG ── */
const SearchIco = () => <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>;
const PinIco    = () => <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>;
const CalIco    = () => <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>;
const XIcon     = () => <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12"/></svg>;
const PhoneIco  = () => <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.63A2 2 0 012 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>;

/* ── Card ── */
const ItemCard = ({ item, onClick, idx }) => {
    const isLost = item.itemType === 'lost';
    return (
        <div onClick={() => onClick(item)} style={{
            background: 'var(--white)', borderRadius: 18,
            border: '1px solid var(--border)',
            overflow: 'hidden', cursor: 'pointer',
            transition: 'all 0.22s ease',
            animation: `stagger 0.4s ease ${(idx % 8) * 45}ms both`,
            boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
        }}
        onMouseEnter={e => {
            e.currentTarget.style.transform = 'translateY(-4px)';
            e.currentTarget.style.boxShadow = '0 12px 40px rgba(0,0,0,0.1)';
            e.currentTarget.style.borderColor = 'rgba(26,23,20,0.2)';
        }}
        onMouseLeave={e => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.04)';
            e.currentTarget.style.borderColor = 'var(--border)';
        }}
        >
            {/* Image */}
            <div style={{ position: 'relative', height: 190, overflow: 'hidden', background: isLost ? '#fde8e4' : 'var(--sage-l)' }}>
                <img src={item.imageUrl} alt={item.itemName || 'Item'}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.3s' }}
                    onError={e => { e.target.src = `https://placehold.co/400x300/${isLost ? 'fde8e4/b33315' : 'e6f0e9/2e5c3a'}?text=${encodeURIComponent(item.itemName || 'Item')}`; }}
                    onMouseEnter={e => e.target.style.transform = 'scale(1.04)'}
                    onMouseLeave={e => e.target.style.transform = 'scale(1)'}
                />
                <span className={`tag ${isLost ? 'tag-lost' : 'tag-found'}`}
                    style={{ position: 'absolute', top: 12, left: 12 }}>
                    {isLost ? '● Lost' : '● Found'}
                </span>
            </div>

            {/* Body */}
            <div style={{ padding: '16px 18px' }}>
                <h3 style={{
                    fontFamily: 'var(--serif)', fontWeight: 400, fontSize: 18,
                    color: 'var(--ink)', marginBottom: 10, lineHeight: 1.2,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>{item.itemName || 'Unnamed Item'}</h3>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--ink-3)', fontSize: 13 }}>
                        <PinIco />{item.location || 'Unknown location'}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--ink-3)', fontSize: 13 }}>
                        <CalIco />{item.createdAt ? new Date(item.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                    </div>
                </div>

                <div style={{
                    marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border-2)',
                    fontSize: 13, color: 'var(--accent)', fontWeight: 500,
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}>
                    <span>View details</span>
                    <span>→</span>
                </div>
            </div>
        </div>
    );
};

/* ── Skeleton ── */
const Skel = () => (
    <div style={{ background: 'var(--white)', borderRadius: 18, border: '1px solid var(--border)', overflow: 'hidden' }}>
        <div className="skel" style={{ height: 190 }} />
        <div style={{ padding: '16px 18px' }}>
            <div className="skel" style={{ height: 20, width: '65%', marginBottom: 12 }} />
            <div className="skel" style={{ height: 13, width: '50%', marginBottom: 8 }} />
            <div className="skel" style={{ height: 13, width: '40%' }} />
        </div>
    </div>
);

/* ── Pill button ── */
const Pill = ({ active, onClick, label, variant = 'default' }) => {
    const colors = {
        default: active ? ['var(--ink)', 'var(--cream)'] : ['transparent', 'var(--ink-3)'],
        lost:    active ? ['#b33315', '#fde8e4'] : ['transparent', 'var(--ink-3)'],
        found:   active ? [ '#2e5c3a', 'var(--sage-l)'] : ['transparent', 'var(--ink-3)'],
    };
    const bg = variant === 'lost' && active ? '#b33315'
             : variant === 'found' && active ? '#2e5c3a'
             : variant === 'default' && active ? 'var(--ink)'
             : 'transparent';
    const col = variant === 'lost' && active ? '#fde8e4'
              : variant === 'found' && active ? 'var(--sage-l)'
              : active ? 'var(--cream)'
              : 'var(--ink-3)';

    return (
        <button onClick={onClick} style={{
            padding: '7px 16px', borderRadius: 99,
            background: bg, color: col,
            border: active ? 'none' : '1.5px solid var(--border)',
            fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: 'var(--sans)',
            transition: 'all 0.15s',
        }}>
            {label}
        </button>
    );
};

function LostItemsGallery() {
    const [items, setItems] = useState([]);
    const [filtered, setFiltered] = useState([]);
    const [selected, setSelected] = useState(null);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [type, setType] = useState('all');
    const [sort, setSort] = useState('newest');
    const [error, setError] = useState(null);
    const [user, setUser] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                try { const u = await Auth.currentAuthenticatedUser(); setUser(u); } catch {}
                const data = await getLostItemsFromDynamoDB();
                setItems(data); setFiltered(data);
            } catch { setError('Could not load items. Please refresh.'); }
            finally { setLoading(false); }
        })();
    }, []);

    useEffect(() => {
        let r = [...items];
        if (type !== 'all') r = r.filter(i => i.itemType === type);
        if (search) {
            const t = search.toLowerCase();
            r = r.filter(i => (i.itemName || '').toLowerCase().includes(t)
                || (i.description || '').toLowerCase().includes(t)
                || (i.location || '').toLowerCase().includes(t));
        }
        if (sort === 'newest') r.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
        if (sort === 'oldest') r.sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));
        if (sort === 'name') r.sort((a, b) => (a.itemName || '').localeCompare(b.itemName || ''));
        setFiltered(r);
    }, [items, search, type, sort]);

    return (
        <div className="page-wrap" style={{ paddingTop: 60 }}>

            {/* Page header */}
            <div style={{ background: 'var(--white)', borderBottom: '1px solid var(--border)', padding: '48px 0 36px' }}>
                <div className="container">
                    <div className="label-xs" style={{ marginBottom: 10 }}>Community board</div>
                    <h1 className="display" style={{ fontSize: 'clamp(32px, 4.5vw, 56px)', marginBottom: 10 }}>
                        Lost &amp; Found
                    </h1>
                    <p style={{ color: 'var(--ink-3)', fontSize: 15, maxWidth: 480 }}>
                        Browse all reported items. Click any card to view contact details and arrange a handover.
                    </p>
                </div>
            </div>

            <div className="container" style={{ padding: '32px 32px 80px' }}>

                {/* Filters */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 28, alignItems: 'center' }}>
                    {/* Search */}
                    <div className="input-wrap" style={{ flex: '1 1 220px', maxWidth: 380 }}>
                        <span className="input-ico" style={{ left: 12, color: 'var(--ink-4)' }}><SearchIco /></span>
                        <input className="field-input icon-l" placeholder="Search items or locations…"
                            value={search} onChange={e => setSearch(e.target.value)} />
                    </div>

                    {/* Type pills */}
                    <div style={{ display: 'flex', gap: 6 }}>
                        <Pill active={type === 'all'} onClick={() => setType('all')} label="All items" />
                        <Pill active={type === 'lost'} onClick={() => setType('lost')} label="● Lost" variant="lost" />
                        <Pill active={type === 'found'} onClick={() => setType('found')} label="● Found" variant="found" />
                    </div>

                    {/* Sort */}
                    <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 13, color: 'var(--ink-4)' }}>Sort</span>
                        <select className="field-input" style={{ width: 'auto', paddingRight: 28, fontSize: 13, cursor: 'pointer' }}
                            value={sort} onChange={e => setSort(e.target.value)}>
                            <option value="newest">Newest first</option>
                            <option value="oldest">Oldest first</option>
                            <option value="name">Name A–Z</option>
                        </select>
                    </div>
                </div>

                {/* Result info */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 22 }}>
                    <p style={{ fontSize: 13, color: 'var(--ink-4)' }}>
                        {loading ? 'Loading…' : `${filtered.length} item${filtered.length !== 1 ? 's' : ''}`}
                    </p>
                    {(search || type !== 'all') && (
                        <button onClick={() => { setSearch(''); setType('all'); }}
                            style={{ fontSize: 13, color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--sans)' }}>
                            Clear filters
                        </button>
                    )}
                </div>

                {error && <div className="alert alert-err" style={{ marginBottom: 24 }}><span>⚠</span>{error}</div>}

                {/* Grid */}
                {loading ? (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 20 }}>
                        {Array.from({ length: 8 }).map((_, i) => <Skel key={i} />)}
                    </div>
                ) : filtered.length === 0 ? (
                    <div style={{
                        textAlign: 'center', padding: '80px 24px',
                        background: 'var(--white)', borderRadius: 24,
                        border: '1px solid var(--border)',
                    }}>
                        <div style={{ fontSize: 48, marginBottom: 16 }}>🔍</div>
                        <h3 className="display" style={{ fontSize: 26, marginBottom: 8 }}>Nothing found</h3>
                        <p style={{ color: 'var(--ink-3)', marginBottom: 24 }}>Try adjusting your search or filters</p>
                        <button onClick={() => { setSearch(''); setType('all'); }} className="btn btn-ink">Reset</button>
                    </div>
                ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 20 }}>
                        {filtered.map((item, i) => <ItemCard key={item.itemId} item={item} onClick={setSelected} idx={i} />)}
                    </div>
                )}
            </div>

            {/* ── Modal ── */}
            {selected && (
                <div className="modal-wrap" onClick={e => { if (e.target === e.currentTarget) setSelected(null); }}>
                    <div className="modal">
                        {/* Image */}
                        <div style={{ position: 'relative', height: 200, overflow: 'hidden' }}>
                            <img src={selected.imageUrl} alt={selected.itemName}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                onError={e => { e.target.src = "https://placehold.co/500x200/f2efe8/7a756e?text=No+Image"; }}
                            />
                            <button onClick={() => setSelected(null)} style={{
                                position: 'absolute', top: 14, right: 14,
                                width: 32, height: 32, borderRadius: '50%',
                                background: 'rgba(255,255,255,0.9)', border: '1px solid var(--border)',
                                color: 'var(--ink)', cursor: 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            }}><XIcon /></button>
                            <span className={`tag ${selected.itemType === 'lost' ? 'tag-lost' : 'tag-found'}`}
                                style={{ position: 'absolute', bottom: 14, left: 18 }}>
                                {selected.itemType === 'lost' ? '● Lost' : '● Found'}
                            </span>
                        </div>

                        {/* Content */}
                        <div style={{ padding: '24px 28px 28px' }}>
                            <h2 className="display" style={{ fontSize: 28, marginBottom: 6 }}>
                                {selected.itemName || 'Unnamed Item'}
                            </h2>
                            <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
                                <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, color: 'var(--ink-3)' }}>
                                    <PinIco />{selected.location || 'Unknown'}
                                </span>
                                <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, color: 'var(--ink-3)' }}>
                                    <CalIco />{selected.createdAt ? new Date(selected.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : '—'}
                                </span>
                            </div>

                            {selected.description && (
                                <p style={{
                                    fontSize: 14, color: 'var(--ink-2)', lineHeight: 1.7, marginBottom: 20,
                                    padding: '14px 16px', background: 'var(--cream)', borderRadius: 10,
                                    border: '1px solid var(--border)',
                                }}>{selected.description}</p>
                            )}

                            {user ? (
                                <div style={{
                                    padding: '16px 18px', background: 'var(--sage-l)',
                                    border: '1px solid #bcd9c6', borderRadius: 14,
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 6 }}>
                                        <PhoneIco />
                                        <span style={{ fontSize: 12, fontWeight: 600, color: '#2e5c3a', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Contact for handover</span>
                                    </div>
                                    <p style={{ fontSize: 16, fontWeight: 600, color: 'var(--ink)', wordBreak: 'break-all' }}>
                                        {selected.contactInfo || 'No contact info provided'}
                                    </p>
                                    <p style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 6 }}>
                                        Reach out to arrange verification and item collection.
                                    </p>
                                </div>
                            ) : (
                                <div style={{
                                    padding: '16px 18px', background: 'var(--amber-l)',
                                    border: '1px solid #f5d8aa', borderRadius: 14,
                                }}>
                                    <p style={{ fontSize: 14, fontWeight: 500, color: '#8a4e0c', marginBottom: 10 }}>
                                        Sign in to view contact details and claim this item.
                                    </p>
                                    <button onClick={() => { Auth.federatedSignIn(); setSelected(null); }}
                                        className="btn btn-ink btn-sm">Sign in now</button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
export default LostItemsGallery;